import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import { stub as $t } from '@nbcnews/analytics-framework';
import styles from './styles.module.scss';

/**
 * Expandable jump link list.
 *
 * @param {object} props
 * @param {string} [props.activeLink=""]
 * @param {Function} [props.callback]
 * @param {string} props.clickTrackingId
 * @param {object[]} props.links
 * @param {number} props.numberOfLinksToShow
 */
const ExpandableJumpLinkList = ({
  activeLink = '',
  callback = () => {},
  clickTrackingId,
  links = [],
  numberOfLinksToShow = 3,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef();

  if (!links.length) return null;

  const hasHiddenLinks = links.length > numberOfLinksToShow;
  const numHiddenLinks = hasHiddenLinks ? links.length - numberOfLinksToShow : 0;

  /**
   * Helper function to handle clicks
   *
   * @param {object} e - Event
   */
  const handleOnClick = (e) => {
    e.preventDefault();

    e.stopPropagation();

    callback();

    $t('track', clickTrackingId, { action: 'jumpLink', name: e.target.textContent });

    window.location.href = e.target.href;
  };

  /**
   * Helper function to format links
   */
  const getLinks = () => {
    if (!hasHiddenLinks || (hasHiddenLinks && isExpanded)) {
      return links.map((link, index) => (
        <li
          className={classNames(
            styles.expandableJumpLinkList__item,
            { [styles.expandableJumpLinkList__activeItem]: activeLink === link.href },
          )}
          key={numberOfLinksToShow + link.href}
        >
          <a
            className={styles.expandableJumpLinkList__itemLink}
            data-testid={`expandable-jump-link-list__item-link-${index}`}
            href={link.href}
            onClick={handleOnClick}
          >
            {link.linkText}
          </a>
        </li>
      ));
    }

    return [
      ...links.slice(0, numberOfLinksToShow).map((link, index) => (
        <li
          className={classNames(
            styles.expandableJumpLinkList__item,
            { [styles.expandableJumpLinkList__activeItem]: activeLink === link.href },
          )}
          key={link.href}
        >
          <a
            className={styles.expandableJumpLinkList__itemLink}
            data-testid={`jump-link-list__item-link-${index}`}
            href={link.href}
            onClick={handleOnClick}
          >
            {link.linkText}
          </a>
        </li>
      )),
      (
        <li
          className={classNames(
            styles.expandableJumpLinkList__Affordance,
            styles.expandableJumpLinkList__item,
          )}
          data-testid="expandable-jump-link-list__affordance"
          inert
          key="affordance"
        >
          <div className={styles.expandableJumpLinkList__Overlay} />
          {links[numberOfLinksToShow].linkText}
        </li>
      ),
      ...links.slice(numberOfLinksToShow + 1).map((link, index) => (
        <li
          className={classNames(
            styles.expandableJumpLinkList__item,
            { [styles.expandableJumpLinkList__activeItem]: activeLink === link.href },
            { [styles['expandableJumpLinkList__item--hidden']]: !isExpanded },
          )}
          key={numberOfLinksToShow + link.href}
        >
          <a
            className={styles.expandableJumpLinkList__itemLink}
            data-testid={`expandable-jump-link-list__item-link-${index}`}
            href={link.href}
            onClick={handleOnClick}
          >
            {link.linkText}
          </a>
        </li>
      )),
    ];
  };

  /**
   * Helper function to expand the list on click
   *
   * @param {boolean} expand
   */
  const handleExpandListClick = ({ expand }) => {
    setIsExpanded(expand);
  };

  return (
    <div
      className={styles.expandableJumpLinkList}
      data-testid="expandable-jump-link-list"
    >
      <ul
        ref={containerRef}
        className={classNames(styles.expandableJumpLinkList__Container,
          { [styles['expandableJumpLinkList__Container--NoExpand']]: !hasHiddenLinks })}
      >
        {getLinks()}
      </ul>
      {
        hasHiddenLinks && !isExpanded && (
          <button
            className={styles.expandableJumpLinkList__Button}
            onClick={() => handleExpandListClick({ expand: true })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setIsExpanded(true);
                setTimeout(() => {
                  containerRef?.current
                    ?.querySelectorAll('li')[numberOfLinksToShow]
                    ?.querySelector('a')
                    ?.focus();
                }, 100);
              }
            }}
            type="button"
          >
            {`See ${numHiddenLinks} More`}
            <span className={`${styles.icon} ${styles.iconDown} icon icon-chevron-right`} />
          </button>
        )
      }

      {
        hasHiddenLinks && isExpanded && (
          <button
            className={styles.expandableJumpLinkList__Button}
            onClick={() => handleExpandListClick({ expand: false })}
            type="button"
          >
            See Less
            <span className={`${styles.icon} ${styles.iconUp} icon icon-chevron-right`} />
          </button>
        )
      }
    </div>
  );
};

export { ExpandableJumpLinkList };
